import merge from 'lodash/merge'
import isEmpty from 'lodash/isEmpty'
import { useCallback, useMemo, useState } from 'react'
import type { IStorage } from '@ztechapps/common.utils'
import { getDynamicDateFilter, jsonTryParse, LocalStorage, SessionStorage, splitObjectByOperators } from '@ztechapps/common.utils'
import type {
  ListAction,
  ListActionResult,
  ListField,
  OnChangeFilterCascadeFunction,
  OnChangeSearchFieldFunction,
  OnChangeSearchTypeFunction,
  OnFilterFunction,
  OnSearchFunction,
  OnSortFunction,
  Search,
} from '../types/QueryInput'
import { HydrationStatusEnum, SearchTypeEnum } from '../types/QueryInput'
import { useDeepCompareEffect } from '../useDeepCompareEffect'
import type { ListActionOptions } from './types'
import { StorageType } from './types'
import type { FilterQuery, Sort, Template } from '@ztechapps/common.contract'
import isNil from 'lodash/isNil'
import assign from 'lodash/assign'

function storageFactory(storageType: StorageType): IStorage<ListAction> {
  switch (storageType) {
    case StorageType.localStorage:
      return new LocalStorage()
    case StorageType.sessionStorage:
      return new SessionStorage()
    default:
      return null
  }
}

const _defaultSort = { _id: 1 }
const _defaultSearch = {}

/**
 * Hook for managing list actions including filtering, sorting, and search.
 * Optionally the state can be stored in localStorage or sessionStorage.
 *
 * @remarks
 * The `__reset` flag can be used in `tableFilter` to trigger a complete reset:
 * - When `tableFilter.__reset.value === true` is passed to `onFilter`:
 *   - Clears all table filters (`tableFilter = {}`)
 *   - Resets sort to `defaultSort` (defaults to `{ _id: 1 }` if not specified)
 *   - Persists the reset state to storage (if storage is configured)
 *
 * @example
 * ```typescript
 * // Trigger a reset
 * onFilter({
 *   tableFilter: { __reset: { value: true } },
 *   searchValue: '',
 *   searchObject: {},
 * })
 * ```
 */
export const useListAction = <T = any>({
  defaultSort = _defaultSort as Sort<T>,
  defaultSearch = _defaultSearch as FilterQuery<T>,
  defaultFilterCascade = true,
  defaultSearchType = SearchTypeEnum.fullText,
  timeZone,
  storageKey,
  storageType,
}: ListActionOptions<T> = {}): ListActionResult => {
  const [sort, setSort] = useState<Sort<T>>(defaultSort)
  const [filter, _setFilter] = useState<FilterQuery<T>>(defaultSearch)
  const [searchValue, setSearchValue] = useState<string>(null)
  const [filterTemplate, setFilterTemplate] = useState<Template>(null)
  const [searchObj, setSearchObj] = useState<Search>(null)
  const [searchField, setSearchField] = useState<ListField>(null)
  const [searchType, setSearchType] = useState<SearchTypeEnum>(defaultSearchType)
  const [cascadeFilter, setCascadeFilter] = useState<boolean>(defaultFilterCascade)
  const [tableFilter, _setTableFilter] = useState<FilterQuery<T>>(null)

  const storage = useMemo(() => storageFactory(storageType), [storageType])
  const [hydrationStatus, setHydrationStatus] = useState(HydrationStatusEnum.hydrating)
  //load the from the storage once, and set the values for states
  useDeepCompareEffect(() => {
    //we need to set the default filter,sort,  and cascadeFilter it will be overridden by the storage values
    // from the storage if the storage exists
    setFilter(defaultSearch)
    setSort(defaultSort)
    setCascadeFilter(defaultFilterCascade)

    //just skip the loading when the storage is not defined
    //if the user did not defined the storage type, storage will be null according the storageFactory
    if (storage === null) return

    if (!storageKey)
      //if  the storage key is not defined, we need a hard throw of error it cannot continue
      throw new Error('Storage key should be defined.')

    const storedListAction = storage.get(storageKey, true) as ListAction

    //now we set here the filter, sort and searchValue, templateId, sortId, from the storedListAction, but set them only when there is a value
    if (!isNil(storedListAction?.filter)) setFilter(storedListAction?.filter)
    if (!isNil(storedListAction?.sort)) setSort(storedListAction?.sort)
    if (!isNil(storedListAction?.searchValue)) setSearchValue(storedListAction?.searchValue)
    if (!isNil(storedListAction?.filterTemplate)) setFilterTemplate(storedListAction?.filterTemplate)
    if (!isNil(storedListAction?.searchObj)) setSearchObj(storedListAction?.searchObj)
    if (!isNil(storedListAction?.searchField)) setSearchField(storedListAction?.searchField)
    if (!isNil(storedListAction?.searchType)) setSearchType(storedListAction?.searchType)
    if (!isNil(storedListAction?.cascadeFilter)) setCascadeFilter(storedListAction?.cascadeFilter)
    if (!isNil(storedListAction?.tableFilter)) setTableFilter(storedListAction?.tableFilter)

    setHydrationStatus(HydrationStatusEnum.hydrated)
  }, [storage, defaultSearch, defaultSort, defaultFilterCascade])

  //we can set here the new value for list action that will trigger the storing of
  //data to storage
  const setListAction = useCallback(
    (newValue: Partial<ListAction<T>>) => {
      //just skip the loading when the storage is not defined
      if (storage === null) return

      //we need to get the latest value from the storage first
      const currentStoredListAction = storage.get(storageKey, true)

      //then build for the new value
      //we need assign here so that the same fields will be overwritten
      //e.g. the whole filter object will be overwritten, but the sort and template will be the same
      const mergedValue = assign({}, currentStoredListAction, newValue)

      //now we store the value
      storage.set(storageKey, mergedValue as ListAction<T>)
    },
    [storage]
  )

  const onSort: OnSortFunction<T> = useCallback(
    ({ sortObject }) => {
      setSort(sortObject?.sort)
      const result = { sort: sortObject?.sort }
      setListAction(result)
      return result
    },
    [setListAction]
  )

  const setFilter = useCallback(
    (value: FilterQuery<T>) => {
      //inject the dynamic date filter
      const filterString = JSON.stringify(value)
      const finalSearch = getDynamicDateFilter({ filterString, timeZone })
      const parsedFinalSearch = jsonTryParse(finalSearch)

      //set the filter value
      _setFilter(parsedFinalSearch)

      return finalSearch
    },
    [timeZone]
  )

  /**
   * Sets or updates the table filter.
   *
   * The tableFilter has structure of `{ fieldName: { <filtercondition> } }`.
   * This function extracts the filter condition and sets it into the tableFilter state.
   *
   * @remarks
   * When `value.__reset.value === true`, triggers a complete reset:
   * - Clears all table filters (`tableFilter = {}`)
   * - Resets sort to `defaultSort` (defaults to `{ _id: 1 }`)
   * - Persists reset state to storage if configured
   *
   * @param value - The filter query to apply, or `{ __reset: { value: true } }` to trigger reset
   */
  const setTableFilter = useCallback(
    (value: FilterQuery<T>) => {
      //we need to use the assign so it will override the whole field itself
      //needs to combine all filters so this assign is needed
      let mergeTableFilter = tableFilter
      let isReset = false

      if (!isNil(value)) {
        //if the value is the meta __reset, we need to clear the table filter and reset sort
        if (value?.__reset?.value === true) {
          mergeTableFilter = {}
          isReset = true

          //reset sort to defaultSort
          setSort(defaultSort)
        } else {
          //else we just need to assign the value of the current tableFilter with the new value
          mergeTableFilter = assign({}, tableFilter, value)
        }
      }

      //we need to check if there is $or, $nor or $and in the table filter, we need to pull it out
      const { withOperators, withoutOperators } = splitObjectByOperators(mergeTableFilter)

      const filterString = JSON.stringify(assign({}, withOperators, withoutOperators))

      const parsedTableFilterAsString = getDynamicDateFilter({ filterString, timeZone })
      const parsedTableFilter = jsonTryParse(parsedTableFilterAsString) as Record<string, any>

      _setTableFilter(mergeTableFilter)

      return { finalFilter: mergeTableFilter, parsedTableFilter, isReset }
    },
    [tableFilter, timeZone, defaultSort]
  )

  //TODO: Remove this as this can be done in onSearch
  const onSearch: OnSearchFunction = useCallback(
    ({ searchObject, searchValue }) => {
      setSearchValue(searchValue)

      //combine the value from the filter and the search object
      searchObject = merge({}, defaultSearch, searchObject)

      let finalSearch = searchObject
      //include the default search on search object
      const searchPredicate = jsonTryParse(filterTemplate?.value)
      if (!isEmpty(searchPredicate)) {
        if (cascadeFilter) {
          finalSearch = { $and: [finalSearch, searchPredicate] }
        } else {
          finalSearch = { $and: [finalSearch] }
        }
      }

      setFilter(finalSearch)
      setSearchObj(searchObject)

      const result = { filter: finalSearch, searchValue, searchObj: searchObject }
      setListAction(result)
      return result
    },
    [defaultSearch, filterTemplate, cascadeFilter, setFilter, setListAction]
  )

  const onFilter: OnFilterFunction = useCallback(
    ({ template, tableFilter: argsTableFilter, searchObject: argsSearchObject, searchValue: argsSearchValue }) => {
      //get the value from the template, this will return empty object if there is no template
      const argsTemplateValue = jsonTryParse(template?.value)
      const templateValue = jsonTryParse(filterTemplate?.value)
      const mergedTemplateFilter = isNil(template) ? templateValue : argsTemplateValue //assign({}, templateValue, argsTemplateValue) //merge the stored and the passed template
      const mergedTemplate = isNil(template) ? filterTemplate : template // assign({}, filterTemplate, template)

      //get the value from the table filter (isReset indicates if __reset flag was triggered)
      const { finalFilter: localTableFilter, parsedTableFilter, isReset } = setTableFilter(argsTableFilter)

      //combine the value from the search object and from the args
      const mergedSearchObject = isNil(argsSearchObject) ? searchObj : argsSearchObject //isEmpty(argsSearchObject) ? {} : assign({}, searchObj, argsSearchObject)

      //merge the default search value
      const mergedSearchValue = argsSearchValue ?? searchValue

      //filter is always cascaded, so we need to combine the results from
      //1. search object
      //2. table filter
      //3. filter template
      //4. default search

      let finalSearch: FilterQuery<T> = { $and: [] }

      //include the filter template
      if (!isEmpty(mergedTemplateFilter)) {
        finalSearch.$and.push(mergedTemplateFilter)
      }

      //merge the search object
      if (!isEmpty(mergedSearchValue) && !isEmpty(mergedSearchObject)) {
        finalSearch.$and.push(mergedSearchObject)
      }

      //merge the table filter
      if (!isEmpty(localTableFilter) && !isEmpty(parsedTableFilter)) {
        finalSearch.$and.push(parsedTableFilter)
      }

      //merge the default search
      if (!isEmpty(defaultSearch)) finalSearch.$and.push(defaultSearch)

      //if the finalSearch.$and is empty, we need to remove it
      if (isEmpty(finalSearch.$and)) finalSearch = {}

      setFilter(finalSearch)
      setFilterTemplate(mergedTemplate)
      setSearchValue(mergedSearchValue)
      setSearchObj(mergedSearchObject)

      const result = {
        filter: finalSearch,
        filterTemplate: mergedTemplate,
        tableFilter: localTableFilter,
        searchValue: mergedSearchValue,
        searchObj: mergedSearchObject,
      }

      //when reset is triggered, also persist the sort reset to storage
      if (isReset) {
        setListAction({ ...result, sort: defaultSort })
      } else {
        setListAction(result)
      }

      return result
    },
    [filterTemplate, searchObj, searchValue, defaultSearch, defaultSort, setFilter, setTableFilter, setListAction]
  )

  const onChangeSearchField: OnChangeSearchFieldFunction = useCallback(
    (args: ListField) => {
      setSearchField(args)
      const result = { searchField: args }
      setListAction(result)
      return result
    },
    [setListAction]
  )

  const onChangeSearchType: OnChangeSearchTypeFunction = useCallback(
    (args: SearchTypeEnum) => {
      setSearchType(args)
      const result = { searchType: args }
      setListAction(result)
      return result
    },
    [setListAction]
  )

  const onChangeFilterCascade: OnChangeFilterCascadeFunction = useCallback(() => {
    setCascadeFilter(!cascadeFilter)
    const result = { cascadeFilter: !cascadeFilter }
    setListAction(result)
    return result
  }, [cascadeFilter, setListAction])

  return useMemo(
    () => ({
      filter,
      sort,
      searchValue,
      onSort,
      onSearch,
      onFilter,
      setFilter,
      setSort,
      setSearchValue,
      setSearchObj,
      searchObj,
      filterTemplate,
      setFilterTemplate,
      searchField,
      setSearchField,
      searchType,
      setSearchType,
      onChangeSearchField,
      onChangeSearchType,
      onChangeFilterCascade,
      cascadeFilter,
      setCascadeFilter,
      hydrationStatus: hydrationStatus,
      storage,
      storageKey,
      tableFilter,
    }),
    [
      filter,
      sort,
      searchValue,
      searchObj,
      filterTemplate,
      searchField,
      searchType,
      cascadeFilter,
      tableFilter,
      hydrationStatus,
      storage,
      storageKey,
      onSort,
      onSearch,
      onFilter,
      setFilter,
      onChangeSearchField,
      onChangeSearchType,
      onChangeFilterCascade,
    ]
  )
}
